import { useState, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Save } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CATEGORY_OPTIONS } from "@/lib/data";
import { updateMenuItem, uploadImage, resolveImg } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border-b border-[#FDFBF7]/20 focus:border-[#D4AF37] outline-none py-2.5 font-body text-[#FDFBF7] placeholder:text-[#A89F91]/50 transition-colors duration-300";
const labelCls = "block font-body text-[11px] tracking-[0.2em] uppercase text-[#A89F91] mb-1";

export default function EditItemDialog({ item, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(item);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // keep local form in sync when a different item is opened
  const [lastId, setLastId] = useState(item?.id);
  if (item && item.id !== lastId) {
    setLastId(item.id);
    setForm(item);
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, image_url: url }));
      toast.success("Image updated");
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) return toast.error("Please enter a dish name.");
    if (!form.half_tray_price && !form.full_tray_price && !form.serves_price)
      return toast.error("Add at least one price.");
    setSaving(true);
    try {
      await updateMenuItem(item.id, {
        category: form.category,
        name: form.name,
        description: form.description || "",
        image_url: form.image_url || null,
        half_tray_price: form.half_tray_price || null,
        full_tray_price: form.full_tray_price || null,
        serves_qty: form.serves_qty || null,
        serves_price: form.serves_price || null,
        order: form.order || 0,
      });
      toast.success("Item updated");
      onSaved();
      onOpenChange(false);
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const categoryKnown = CATEGORY_OPTIONS.includes(form.category);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-item-dialog" className="border-[#D4AF37]/25 bg-[#140A0D] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#FDFBF7]">Edit Dish</DialogTitle>
          <DialogDescription className="font-body text-sm text-[#A89F91]">Update this menu item's details, pricing and photo.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className={labelCls}>Category</label>
            <select data-testid="edit-category" className={`${inputCls} appearance-none cursor-pointer`} value={categoryKnown ? form.category : "__custom"} onChange={(e) => e.target.value !== "__custom" && setForm((f) => ({ ...f, category: e.target.value }))}>
              {CATEGORY_OPTIONS.map((c) => <option key={c} value={c} className="bg-[#140A0D]">{c}</option>)}
              {!categoryKnown && <option value="__custom" className="bg-[#140A0D]">{form.category}</option>}
            </select>
            <input className={`${inputCls} mt-2`} placeholder="or type category" value={form.category} onChange={set("category")} />
          </div>
          <div>
            <label className={labelCls}>Dish Name *</label>
            <input data-testid="edit-name" className={inputCls} value={form.name} onChange={set("name")} />
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <textarea data-testid="edit-description" rows={2} className={`${inputCls} resize-none`} value={form.description || ""} onChange={set("description")} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={labelCls}>Half Tray $</label><input data-testid="edit-half-price" className={inputCls} value={form.half_tray_price || ""} onChange={set("half_tray_price")} /></div>
            <div><label className={labelCls}>Full Tray $</label><input data-testid="edit-full-price" className={inputCls} value={form.full_tray_price || ""} onChange={set("full_tray_price")} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={labelCls}>Serves Qty</label><input data-testid="edit-serves-qty" className={inputCls} value={form.serves_qty || ""} onChange={set("serves_qty")} /></div>
            <div><label className={labelCls}>Serves $</label><input data-testid="edit-serves-price" className={inputCls} value={form.serves_price || ""} onChange={set("serves_price")} /></div>
          </div>
          <div>
            <label className={labelCls}>Photo</label>
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
            <button type="button" onClick={() => fileRef.current?.click()} className="w-full flex items-center justify-center gap-2 border border-dashed border-[#D4AF37]/40 rounded-lg py-3 text-[#A89F91] hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors font-body text-sm">
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              {uploading ? "Uploading..." : "Change photo"}
            </button>
            {form.image_url && (
              <div className="mt-3 h-32 w-full overflow-hidden rounded-lg border border-[#D4AF37]/20">
                <img src={resolveImg(form.image_url)} alt="preview" className="w-full h-full object-cover" />
              </div>
            )}
          </div>
          <button data-testid="edit-item-submit" type="submit" disabled={saving} className="w-full flex items-center justify-center gap-2 px-8 py-3.5 bg-[#D4AF37] text-[#0A0607] font-body text-sm tracking-[0.2em] uppercase disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Changes
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
